<script lang="ts">
  import type { CostRow } from '../lib/cost-row';
  import { CostColumn } from '../lib/cost-column';
  import type { CostsFilter } from '../lib/costs-filter';
  import CostTableRow from './CostTableRow.svelte';

  interface Props {
    firstColumn: string;
    rows: CostRow[];
    filter: CostsFilter;
    onSort: (patch: Record<string, string | null>) => void;
    onOpen: (row: CostRow) => void;
    onClearFilters: () => void;
  }

  let { firstColumn, rows, filter, onSort, onOpen, onClearFilters }: Props = $props();

  let aberta = $state<Set<string>>(new Set());

  /** Só a coluna ordenada mostra seta — as outras ficam sem ruído até serem clicadas. */
  function arrowOf(column: CostColumn): string {
    if (!filter.column.is(column)) return '';
    return filter.descending ? ' ↓' : ' ↑';
  }

  function alternar(key: string): void {
    const proxima = new Set(aberta);
    if (proxima.has(key)) {
      proxima.delete(key);
      aberta = proxima;
      return;
    }
    proxima.add(key);
    aberta = proxima;
  }
</script>

<div class="scroller">
  <table class="grid-table">
    <thead>
      <tr>
        <th aria-sort={filter.directionOf(CostColumn.NAME)}>
          <button class="sorter" onclick={() => onSort(filter.sortPatch(CostColumn.NAME))}>
            {firstColumn}<span class="arrow" aria-hidden="true"
              >{arrowOf(CostColumn.NAME)}</span
            >
          </button>
        </th>
        {#each CostColumn.numeric() as column (column.slug)}
          <th class="num" aria-sort={filter.directionOf(column)}>
            <button class="sorter" onclick={() => onSort(filter.sortPatch(column))}>
              {column.label}<span class="arrow" aria-hidden="true">{arrowOf(column)}</span>
            </button>
          </th>
        {/each}
      </tr>
    </thead>
    <tbody>
      {#each rows as row (row.key)}
        <CostTableRow
          {row}
          isOpen={aberta.has(row.key)}
          onToggle={() => alternar(row.key)}
          onOpen={() => onOpen(row)}
        />
        {#if aberta.has(row.key)}
          {#each row.children as child (child.key)}
            <CostTableRow row={child} isChild onOpen={() => onOpen(child)} />
          {/each}
        {/if}
      {/each}
      {#if rows.length === 0}
        <tr>
          <td colspan="8" class="empty">
            <span class="empty-icon" aria-hidden="true">∅</span>
            {#if filter.isEmpty}
              <span class="empty-title">Sem custo no período</span>
              <span class="empty-sub">Nenhuma chamada de IA entre as datas escolhidas.</span>
            {:else}
              <span class="empty-title">Nada casa com o filtro</span>
              <button class="empty-action" onclick={onClearFilters}>Limpar filtros</button>
            {/if}
          </td>
        </tr>
      {/if}
    </tbody>
  </table>
</div>

<style>
  /* 8 colunas não cabem em tela estreita: rola a tabela, não a página. */
  .scroller {
    overflow-x: auto;
  }
  .grid-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    min-width: 860px;
  }
  .sorter {
    display: inline-flex;
    align-items: baseline;
    gap: 2px;
    padding: 0;
    font: inherit;
    color: inherit;
    letter-spacing: inherit;
    text-transform: inherit;
    background: none;
    border: none;
    cursor: pointer;
  }
  .sorter:hover {
    color: var(--text-secondary);
  }
  .sorter:focus-visible {
    outline: 2px solid var(--color-success);
    outline-offset: 2px;
  }
  .arrow {
    color: var(--color-success);
  }
  .num .sorter {
    justify-content: flex-end;
  }
  .empty-action {
    display: inline-block;
    margin-top: var(--space-3);
    font-size: 12px;
    color: var(--text-secondary);
    background: none;
    border: none;
    cursor: pointer;
    text-decoration: underline;
  }
  .empty-action:focus-visible {
    outline: 2px solid var(--color-success);
    outline-offset: 2px;
  }
  .grid-table th {
    position: sticky;
    top: 0;
    z-index: 1;
    text-align: left;
    padding: var(--space-3) var(--space-4);
    background: var(--bg-base, var(--bg-elevated));
    color: var(--text-tertiary);
    font-weight: 600;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 1px solid var(--border-subtle);
  }
  .num {
    text-align: right;
  }
  .empty {
    text-align: center;
    padding: var(--space-7) var(--space-3);
    border-bottom: none;
  }
  .empty-icon {
    display: block;
    font-size: 22px;
    color: var(--text-tertiary);
    margin-bottom: var(--space-2);
  }
  .empty-title {
    display: block;
    color: var(--text-secondary);
    font-size: 13px;
  }
  .empty-sub {
    display: block;
    color: var(--text-tertiary);
    font-size: 12px;
    margin-top: 2px;
  }
</style>
